import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Direction } from '@/types/game'
import type { LevelMeta } from '@/game/levels'
import {
  buildGridState,
  cloneState,
  resolveSlide,
  findTile,
  hintNextSlide,
  type GridState,
  type SlideEvent,
  type Vec,
} from '@/game/engine'

export type GamePhase = 'intro' | 'idle' | 'sliding' | 'won' | 'dead'

export interface EngineSnapshot {
  phase: GamePhase
  pos: Vec
  path: Vec[]
  slides: number
  coins: number
  gems: number
  keys: number
  cells: string[]
  lastEvents: SlideEvent[]
  shieldActive: boolean
  slowmo: boolean
  invincibleLeft: number
  facing: Direction
}

export interface UndoFrame {
  state: GridState
  pos: Vec
  slides: number
  coins: number
  gems: number
}

interface EngineHandlers {
  onEvent?: (ev: SlideEvent) => void
  onSlideStart?: (dir: Direction) => void
  onSlideEnd?: (result: { died: boolean; reachedHome: boolean; hitWall: boolean }) => void
  onWin?: () => void
  onDeath?: () => void
  onRespawn?: () => void
}

export interface EngineOptions {
  magnetRadius?: number
  speedMult?: number
  autoShield?: boolean
  autoSlowmo?: boolean
  respawnDelayMs?: number
}

export function useGameEngine(level: LevelMeta, handlers: EngineHandlers = {}, options: EngineOptions = {}) {
  const startPos = useMemo(() => {
    const s = buildGridState(level.grid)
    return findTile(s.cells, 'o') ?? { x: 0, y: 0 }
  }, [level.grid])

  const initialState = useMemo(() => {
    const s = buildGridState(level.grid)
    const st = findTile(s.cells, 'o')
    if (st) s.cells[st.y][st.x] = '-'
    return s
  }, [level.grid])

  const stateRef = useRef<GridState>(cloneState(initialState))
  const posRef = useRef<Vec>({ ...startPos })
  const undoStackRef = useRef<UndoFrame[]>([])
  const phaseRef = useRef<GamePhase>('intro')
  const shieldRef = useRef(false)
  const slowmoRef = useRef(false)
  const magnetRef = useRef(false)
  const invincibleRef = useRef(0)
  const facingRef = useRef<Direction>('down')
  const snapshotSlidesRef = useRef(0)
  const snapshotCoinsRef = useRef(0)
  const snapshotGemsRef = useRef(0)
  const commitTimerRef = useRef<number>(0)
  const respawnTimerRef = useRef<number>(0)

  const handlersRef = useRef(handlers)
  handlersRef.current = handlers
  const optionsRef = useRef(options)
  optionsRef.current = options

  const [snapshot, setSnapshot] = useState<EngineSnapshot>(() => ({
    phase: 'intro',
    pos: { ...startPos },
    path: [{ ...startPos }],
    slides: 0,
    coins: 0,
    gems: 0,
    keys: 0,
    cells: initialState.cells.map((r) => r.join('')),
    lastEvents: [],
    shieldActive: false,
    slowmo: false,
    invincibleLeft: 0,
    facing: 'down',
  }))

  const reset = useCallback(() => {
    if (commitTimerRef.current) {
      window.clearTimeout(commitTimerRef.current)
      commitTimerRef.current = 0
    }
    stateRef.current = cloneState(initialState)
    posRef.current = { ...startPos }
    undoStackRef.current = []
    phaseRef.current = 'intro'
    shieldRef.current = optionsRef.current.autoShield ?? false
    slowmoRef.current = optionsRef.current.autoSlowmo ?? false
    magnetRef.current = false
    invincibleRef.current = 0
    facingRef.current = 'down'
    snapshotSlidesRef.current = 0
    snapshotCoinsRef.current = 0
    snapshotGemsRef.current = 0
    setSnapshot({
      phase: 'intro',
      pos: { ...startPos },
      path: [{ ...startPos }],
      slides: 0,
      coins: 0,
      gems: 0,
      keys: 0,
      cells: initialState.cells.map((r) => r.join('')),
      lastEvents: [],
      shieldActive: shieldRef.current,
      slowmo: slowmoRef.current,
      invincibleLeft: 0,
      facing: 'down',
    })
  }, [initialState, startPos])

  useEffect(() => {
    reset()
  }, [reset])

  const begin = useCallback(() => {
    phaseRef.current = 'idle'
    setSnapshot((s) => ({ ...s, phase: 'idle' }))
  }, [])

  const respawn = useCallback(() => {
    if (respawnTimerRef.current) window.clearTimeout(respawnTimerRef.current)
    reset()
    const delay = optionsRef.current.respawnDelayMs ?? 420
    respawnTimerRef.current = window.setTimeout(() => {
      respawnTimerRef.current = 0
      phaseRef.current = 'idle'
      setSnapshot((s) => ({ ...s, phase: 'idle' }))
      handlersRef.current.onRespawn?.()
    }, delay)
  }, [reset])

  useEffect(() => {
    return () => {
      if (commitTimerRef.current) window.clearTimeout(commitTimerRef.current)
      if (respawnTimerRef.current) window.clearTimeout(respawnTimerRef.current)
    }
  }, [level.index])

  const publish = useCallback(
    (extra: Partial<EngineSnapshot>) => {
      setSnapshot((s) => ({
        ...s,
        pos: { ...posRef.current },
        keys: stateRef.current.keys,
        cells: stateRef.current.cells.map((r) => r.join('')),
        shieldActive: shieldRef.current,
        slowmo: slowmoRef.current,
        invincibleLeft: invincibleRef.current,
        facing: facingRef.current,
        ...extra,
      }))
    },
    [],
  )

  const collectAround = useCallback((center: Vec, radius: number): SlideEvent[] => {
    if (radius <= 0) return []
    const st = stateRef.current
    const picked: SlideEvent[] = []
    for (let y = Math.max(0, center.y - radius); y <= Math.min(st.rows - 1, center.y + radius); y++) {
      for (let x = Math.max(0, center.x - radius); x <= Math.min(st.cells[y].length - 1, center.x + radius); x++) {
        const t = st.cells[y][x]
        if (t === 'p') {
          st.cells[y][x] = '-'
          snapshotCoinsRef.current += 1
          picked.push({ kind: 'coin', x, y })
        } else if (t === 'g') {
          st.cells[y][x] = '-'
          snapshotGemsRef.current += 1
          picked.push({ kind: 'gem', x, y })
        }
      }
    }
    return picked
  }, [])

  const slide = useCallback(
    (dir: Direction) => {
      if (phaseRef.current !== 'idle') return
      const working = cloneState(stateRef.current)
      const from = { ...posRef.current }
      const result = resolveSlide(working, from, dir)

      if (
        result.end.x === from.x &&
        result.end.y === from.y &&
        !result.reachedHome &&
        result.events.length === 0
      ) {
        facingRef.current = dir
        publish({ facing: dir })
        handlersRef.current.onSlideEnd?.({ died: false, reachedHome: false, hitWall: true })
        return
      }

      undoStackRef.current.push({
        state: cloneState(stateRef.current),
        pos: { ...from },
        slides: snapshotSlidesRef.current,
        coins: snapshotCoinsRef.current,
        gems: snapshotGemsRef.current,
      })
      if (undoStackRef.current.length > 20) undoStackRef.current.shift()

      let died = result.died
      if (died && invincibleRef.current > 0) {
        died = false
      } else if (died && shieldRef.current) {
        shieldRef.current = false
        died = false
      }

      let coinGain = 0
      let gemGain = 0
      for (const ev of result.events) {
        if (ev.kind === 'coin') coinGain++
        if (ev.kind === 'gem') gemGain++
        handlersRef.current.onEvent?.(ev)
      }

      phaseRef.current = 'sliding'
      facingRef.current = dir
      handlersRef.current.onSlideStart?.(dir)
      setSnapshot((s) => ({ ...s, phase: 'sliding', path: result.path, lastEvents: result.events, facing: dir }))

      const finalPos = result.end

      const commit = () => {
        commitTimerRef.current = 0
        stateRef.current = working
        posRef.current = { ...finalPos }
        snapshotSlidesRef.current += 1
        snapshotCoinsRef.current += coinGain
        snapshotGemsRef.current += gemGain
        if (invincibleRef.current > 0) invincibleRef.current -= 1

        if (result.reachedHome) {
          phaseRef.current = 'won'
          publish({ phase: 'won', slides: snapshotSlidesRef.current, coins: snapshotCoinsRef.current, gems: snapshotGemsRef.current })
          handlersRef.current.onSlideEnd?.({ died: false, reachedHome: true, hitWall: false })
          handlersRef.current.onWin?.()
          return
        }
        if (died) {
          phaseRef.current = 'dead'
          publish({ phase: 'dead', slides: snapshotSlidesRef.current, coins: snapshotCoinsRef.current, gems: snapshotGemsRef.current })
          handlersRef.current.onSlideEnd?.({ died: true, reachedHome: false, hitWall: false })
          handlersRef.current.onDeath?.()
          return
        }

        const radius = optionsRef.current.magnetRadius ?? 0
        const picked = collectAround(finalPos, radius)
        for (const ev of picked) handlersRef.current.onEvent?.(ev)

        phaseRef.current = 'idle'
        const extra: Partial<EngineSnapshot> = {
          phase: 'idle',
          slides: snapshotSlidesRef.current,
          coins: snapshotCoinsRef.current,
          gems: snapshotGemsRef.current,
        }
        if (picked.length) extra.lastEvents = picked
        publish(extra)
        handlersRef.current.onSlideEnd?.({ died: false, reachedHome: false, hitWall: result.stopReason === 'wall' || result.stopReason === 'edge' })
      }

      const speedMult = Math.max(0.25, optionsRef.current.speedMult ?? 1)
      const perTile = slowmoRef.current ? 150 : 62 / speedMult
      const duration = Math.max(160, Math.min(900, result.path.length * perTile))
      commitTimerRef.current = window.setTimeout(commit, duration)
    },
    [publish, collectAround],
  )

  const undo = useCallback(() => {
    if (phaseRef.current !== 'idle') return false
    const frame = undoStackRef.current.pop()
    if (!frame) return false
    stateRef.current = cloneState(frame.state)
    posRef.current = { ...frame.pos }
    snapshotSlidesRef.current = frame.slides
    snapshotCoinsRef.current = frame.coins
    snapshotGemsRef.current = frame.gems
    publish({ slides: frame.slides, coins: frame.coins, gems: frame.gems, phase: 'idle' })
    return true
  }, [publish])

  const activateShield = useCallback(() => {
    shieldRef.current = true
    publish({ shieldActive: true })
  }, [publish])

  const activateSlowmo = useCallback(() => {
    slowmoRef.current = true
    publish({ slowmo: true })
  }, [publish])

  const activateInvincibility = useCallback(
    (slides = 3) => {
      invincibleRef.current = Math.max(invincibleRef.current, slides)
      publish({ invincibleLeft: invincibleRef.current })
    },
    [publish],
  )

  const activateMagnet = useCallback(() => {
    const st = stateRef.current
    let gained = 0
    let gems = 0
    for (let y = 0; y < st.rows; y++) {
      for (let x = 0; x < st.cells[y].length; x++) {
        if (st.cells[y][x] === 'p') {
          st.cells[y][x] = '-'
          gained++
          handlersRef.current.onEvent?.({ kind: 'coin', x, y })
        } else if (st.cells[y][x] === 'g') {
          st.cells[y][x] = '-'
          gems++
          handlersRef.current.onEvent?.({ kind: 'gem', x, y })
        }
      }
    }
    snapshotCoinsRef.current += gained
    snapshotGemsRef.current += gems
    publish({ coins: snapshotCoinsRef.current, gems: snapshotGemsRef.current })
    return gained + gems
  }, [publish])

  const getHint = useCallback((): Direction | null => {
    return hintNextSlide(
      stateRef.current.cells,
      posRef.current,
      stateRef.current.keys,
    )
  }, [])

  const canUndo = useCallback(() => undoStackRef.current.length > 0, [])

  return {
    snapshot,
    startPos,
    begin,
    slide,
    reset,
    respawn,
    undo,
    canUndo,
    activateShield,
    activateSlowmo,
    activateInvincibility,
    activateMagnet,
    getHint,
  }
}
